import type { FluidLightShader } from "../../core/shaders/fluid-light";
import { DevtoolsInstance } from "../../core/base-shader";
import { downloadCanvasAsPNG } from "../../utils";

export async function attachFluidLightDevtools(
  shader: FluidLightShader
): Promise<DevtoolsInstance> {
  // Dynamic import to reduce bundle size
  const [{ Pane }, EssentialsPlugin] = await Promise.all([
    import("tweakpane"),
    import("@tweakpane/plugin-essentials"),
  ]);

  const params = shader.getParams();
  const { lightWave, echo } = params;

  const pane = new Pane({
    title: "Fluid Light",
    expanded: true,
  });
  pane.registerPlugin(EssentialsPlugin);

  const fpsGraph = pane.addBlade({
    view: "fpsgraph",
    rows: 2,
  });

  const tab = pane.addTab({
    pages: [{ title: "Parameters" }, { title: "Advanced" }],
  });

  tab.pages[0].addBinding(params, "side", {
    label: "Side",
    options: {
      Bottom: 0,
      Top: 1,
      Right: 2,
      Left: 3,
    },
  });
  tab.pages[0].addBinding(params, "background", {
    label: "Background",
  });

  tab.pages[0].addBinding(lightWave, "amplitude", {
    label: "Amplitude",
    min: 0.01,
    max: 0.2,
    step: 0.01,
  });
  tab.pages[0].addBinding(lightWave, "speed", {
    label: "Speed",
    min: 0.01,
    max: 1.0,
    step: 0.01,
  });
  tab.pages[0].addBinding(lightWave, "frequency", {
    label: "Frequency",
    min: 0.01,
    max: 2.0,
    step: 0.01,
  });
  tab.pages[0].addBinding(lightWave, "color1", { label: "Color 1" });
  tab.pages[0].addBinding(lightWave, "color2", { label: "Color 2" });
  tab.pages[0].addBinding(lightWave, "color3", { label: "Color 3" });
  tab.pages[0].addBinding(lightWave, "color4", { label: "Color 4" });
  tab.pages[0].addBinding(lightWave, "color5", { label: "Color 5" });
  tab.pages[0].addBinding(lightWave, "minFalloff", {
    label: "Min Falloff",
    min: 1.0,
    max: 10.0,
    step: 0.01,
  });
  tab.pages[0].addBinding(lightWave, "maxFalloff", {
    label: "Max Falloff",
    min: 1.0,
    max: 10.0,
    step: 0.01,
  });

  const echoFolder = tab.pages[0].addFolder({
    title: "Echo",
    expanded: false,
  });
  echoFolder.addBinding(echo, "enabled", { label: "Enabled" });
  echoFolder.addBinding(echo, "offset", {
    label: "Offset",
    min: 0.0,
    max: 0.5,
    step: 0.01,
  });
  echoFolder.addBinding(echo, "radius", {
    label: "Radius",
    min: 0.01,
    max: 1.0,
    step: 0.01,
  });
  echoFolder.addBinding(echo, "frequency", {
    label: "Frequency",
    min: 1.0,
    max: 40.0,
    step: 0.5,
  });
  echoFolder.addBinding(echo, "ringSize", {
    label: "Thickness",
    min: 0.1,
    max: 4.0,
    step: 0.1,
  });
  echoFolder.addBinding(echo, "strokeOpacity", {
    label: "Stroke Opacity",
    min: 0.0,
    max: 1.0,
    step: 0.01,
  });
  echoFolder.addBinding(echo, "fillOpacity", {
    label: "Fill Opacity",
    min: 0.0,
    max: 0.1,
    step: 0.01,
  });

  const echoAnimationFolder = echoFolder.addFolder({
    title: "Animation",
    expanded: false,
  });

  let animationStartTime = performance.now();

  echoAnimationFolder
    .addBinding(echo, "animateIn", { label: "Enabled" })
    .on("change", () => {
      animationStartTime = performance.now();
    });

  if (echo.animateIn) {
    echoAnimationFolder
      .addBinding(echo, "animationDuration", {
        label: "Duration",
        min: 500,
        max: 5000,
        step: 100,
      })
      .on("change", () => {
        animationStartTime = performance.now();
      });
    echoAnimationFolder
      .addBinding(echo, "animationDelay", {
        label: "Delay",
        min: 0,
        max: 2000,
        step: 100,
      })
      .on("change", () => {
        animationStartTime = performance.now();
      });
  }

  // Advanced tab
  tab.pages[1].addButton({ title: "Reset" }).on("click", () => {
    shader.reset();
    pane.refresh();
    animationStartTime = performance.now();
  });

  tab.pages[1].addButton({ title: "Export Config" }).on("click", () => {
    const state = JSON.stringify(pane.exportState(), null, 2);
    const blob = new Blob([state], { type: "application/json" });
    const link = document.createElement("a");
    link.download = "fluid-light-config.json";
    link.href = URL.createObjectURL(blob);
    link.click();
    URL.revokeObjectURL(link.href);
  });

  tab.pages[1].addButton({ title: "Import Config" }).on("click", () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const state = JSON.parse(e.target?.result as string);
            pane.importState(state);
            animationStartTime = performance.now();
          } catch (err) {
            console.error("Failed to import config:", err);
            alert("Failed to import config. Please check the file format.");
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  });

  tab.pages[1].addButton({ title: "Download PNG" }).on("click", () => {
    // Note: This assumes the canvas has id 'canvas' - could be improved
    const canvas = document.querySelector("canvas") as HTMLCanvasElement;
    if (canvas) {
      downloadCanvasAsPNG({ canvas, renderer: null });
    }
  });

  pane.on("change", () => {
    shader.updateParams(params);
  });

  return {
    pane,
    fpsGraph,
    dispose: () => {
      pane.dispose();
    },
  };
}
